
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import modelHelper from "./modelHelperNew";
import BaseField from "./baseFieldNew";
import { useModel, useEmptyClearFilterModel } from "../../contexts/modelContextNew";
import { IPropsField, IPropsInputChange } from "./fieldPropertiesInterfaceNew";
import { ReactComponent as CalendarIconSVG } from "itd-common/assets/icons/menu/calendar.svg";
import { ReactComponent as Arrow } from "itd-common/assets/icons/arrow.svg";
import { t } from "i18next";

interface IPropsRange {
  range?: boolean;
}

const T_PREFIX: string = "pages.dateFieldSettings.";

type IPropsDateField = IPropsField & IPropsRange & IPropsInputChange;

// Tipo mínimo del ref interno del DatePicker (solo lo que usamos)
type DatePickerRef = {
  openCalendar: () => void;
  closeCalendar: () => void;
  isOpen: boolean;
};

// Tipo del ref que exponemos al padre
export type DateFieldRef = {
  open: () => void;
  close: () => void;
};

const DateField = forwardRef<DateFieldRef, IPropsDateField>(function DateField(
  {
    model,
    attr,
    value,
    label,
    onChange,
    disabled,
    required,
    range
  }: IPropsDateField,
  ref
) {
  const modelContext = useModel();
  const emptyClearStates = useEmptyClearFilterModel();
  const modelState = model || modelContext;
  const { getValue, singleClearState } = modelHelper({
    model: modelState,
    attr,
    value,
    onChange,
    emptyClearStates
  });
  const [state, setState] = modelState;

  const datePickerRef = useRef<DatePickerRef | null>(null);

  // Exponemos métodos al padre
  useImperativeHandle(
    ref,
    () => ({
      open: () => datePickerRef.current?.openCalendar(),
      close: () => datePickerRef.current?.closeCalendar()
    }),
    []
  );

  const onValueChange = (event: DateObject[] | any) => {
    let finalEvent = event;

    // al completar un rango nuevo, si el usuario no tocó la hora, forzamos 00:00:00 / 23:59:59
    if (Array.isArray(event) && event.length === 2 && event[0] && event[1]) {
      const isDefaultTime = (d: DateObject) => d.hour === 0 && d.minute === 0 && d.second === 0;
      const from = event[0] as DateObject;
      const to = event[1] as DateObject;
      finalEvent = [
        isDefaultTime(from) ? Object.assign(new DateObject(from), { hour: 0, minute: 0, second: 0 }) : from,
        isDefaultTime(to) ? Object.assign(new DateObject(to), { hour: 23, minute: 59, second: 59 }) : to
      ];
    }

    if (state && attr) {
      const modelWithChange = { ...state };
      modelWithChange[attr] = finalEvent;
      setState(modelWithChange);
    } else if (onChange) {
      onChange(finalEvent);
    }
  };

  const handleIconClick = () => {
    if (datePickerRef.current && !datePickerRef.current.isOpen) {
      datePickerRef.current.openCalendar();
    }
  };

  const spanishMonths = Array.from({ length: 12 }, (_, i) =>
    t(`${T_PREFIX}months.${i + 1}.label`)
  );
  const spanishDays = Array.from({ length: 7 }, (_, i) =>
    t(`${T_PREFIX}days.${i + 1}.label`)
  );

  return (
    <BaseField
      prefix={
        <div
          className="m-2 bg-white w-100"
          onClick={handleIconClick}
          style={{ cursor: "pointer" }}
        >
          <CalendarIconSVG height={18} width={18} />
        </div>
      }
      attr={attr}
      label={label}
      className="notranslate"
      required={required}
      value={getValue()}
      sufix={<Arrow onClick={handleIconClick} style={{ cursor: "pointer" }} />}
      onClearState={singleClearState}
    >
      <DatePicker
        disabled={disabled}
        ref={datePickerRef}
        inputClass="date-picker"
        containerClassName="date-picker-container"
        format="DD/MM/YYYY"
        value={getValue() as string}
        onChange={onValueChange}
        plugins={[<TimePicker position="bottom" />]}
        placeholder=""
        range={range}
        months={spanishMonths}
        weekDays={spanishDays}
        className="notranslate"
        editable={false}
      />
    </BaseField>
  );
});


export default DateField;